/**
 * ItemSpotlightDrawer — Phase 2
 *
 * Listens for 'item_spotlighted' and 'item_spotlight_cleared' session_events
 * and renders a slide-in side drawer with the spotlighted item's details.
 * Participants can dismiss it but it stays visible while spotlight is active.
 * On opening it reads the latest spotlight event, so someone who joins while
 * an item is spotlighted sees it.
 */

import { useEffect, useState } from 'react';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { fetchLatestSessionEvent } from '../../../lib/sessionEvents';
import { C } from '../theme/participantTokens';

interface ItemSpotlightDrawerProps {
  sessionId: string;
  /** Set for a guest by link (S-8): the spotlight as the guest's room read reports it. Nothing is subscribed. */
  current?: SpotlightPayload | null;
}

interface SpotlightPayload {
  itemId: string;
  itemTitle: string;
  note?: string;
}

export function ItemSpotlightDrawer({ sessionId, current }: ItemSpotlightDrawerProps) {
  const [liveSpotlight, setSpotlight] = useState<SpotlightPayload | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const controlled = current !== undefined;
  const spotlight = controlled ? current : liveSpotlight;
  const controlledTitle = controlled ? current?.itemTitle ?? null : null;

  // A new spotlight shows again even if the last one was dismissed.
  useEffect(() => {
    if (controlled) setDismissed(false);
  }, [controlled, controlledTitle]);

  useEffect(() => {
    if (controlled) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let cancelled = false;

    // Catch up: an item already spotlighted when this screen opens.
    void fetchLatestSessionEvent(sessionId, ['item_spotlighted', 'item_spotlight_cleared']).then(latest => {
      if (cancelled || !latest) return;
      setSpotlight(latest.event_type === 'item_spotlighted' ? (latest.payload as unknown as SpotlightPayload) : null);
    });

    const ch = supabase
      .channel(`spotlight:${sessionId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${sessionId}` },
        payload => {
          const ev = payload.new as { event_type: string; payload: SpotlightPayload };
          if (ev.event_type === 'item_spotlighted') {
            setSpotlight(ev.payload);
            setDismissed(false);
          } else if (ev.event_type === 'item_spotlight_cleared') {
            setSpotlight(null);
            setDismissed(false);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(ch);
    };
  }, [sessionId, controlled]);

  const isVisible = spotlight && !dismissed;

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: isVisible ? 16 : -320,
        transform: 'translateY(-50%)',
        zIndex: 95,
        width: 280,
        background: C.bgRaised,
        border: `1px solid ${C.mustard}`,
        borderRadius: 8,
        boxShadow: '0 4px 24px rgba(0,0,0,0.22)',
        padding: '16px 18px',
        transition: 'left 0.28s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>✦</span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '0.52rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: C.mustard,
              marginBottom: 4,
            }}
          >
            Facilitator spotlight
          </div>
          <div
            style={{
              fontFamily: 'Spectral, Georgia, serif',
              fontSize: '1rem',
              fontWeight: 600,
              color: C.textPri,
              lineHeight: 1.3,
            }}
          >
            {spotlight?.itemTitle}
          </div>
          {spotlight?.note && (
            <div
              style={{
                marginTop: 8,
              fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
              fontSize: '0.78rem',
              color: C.textSec,
              lineHeight: 1.5,
              }}
            >
              {spotlight.note}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss spotlight"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: C.textDim,
            fontSize: '0.9rem',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
      <div
        style={{
        fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
        fontSize: '0.68rem',
        color: C.textDim,
        }}
      >
        The facilitator has highlighted this item for discussion.
      </div>
    </div>
  );
}
